import React from 'react';
import { useTranslation } from 'react-i18next';

import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	Paper,
	Typography,
} from '@mui/material';

import { MAX_LESSONS } from '@common/constants/academyReworkings';
import { parseDate } from '@common/helpers/academyReworkings';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { nextButtonSx } from '../../styled';

export default function Lessons({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	return (
		<Box display="flex" flexDirection="column" gap="16px">
			<Typography variant="h3">{t('academyReworkings.chooseLessons')}</Typography>
			<Typography color="text.secondary">{t('academyReworkings.lessonsHint')}</Typography>
			{!flow.lessons.length && <Alert severity="info">{t('academyReworkings.noLessons')}</Alert>}
			{flow.lessons.map((lesson) => {
				const checked = flow.selectedLessons.some(
					(item) => item.gradebook_id === lesson.gradebook_id,
				);
				const missedDate = lesson.schedule_lesson_start
					? parseDate(lesson.schedule_lesson_start)
					: null;
				return (
					<Paper
						key={lesson.gradebook_id}
						variant="outlined"
						sx={{
							px: 2,
							py: 1,
							borderRadius: '16px',
							borderColor: checked ? 'primary.main' : 'divider',
						}}
					>
						<FormControlLabel
							sx={{
								m: 0,
								width: '100%',
								minWidth: 0,
								alignItems: 'flex-start',
								gap: '12px',
								py: 1,
								'& .MuiCheckbox-root': { p: 0, mt: '2px', flexShrink: 0 },
							}}
							control={
								<Checkbox
									checked={checked}
									onChange={() => flow.toggleLesson(lesson)}
									disabled={!checked && flow.selectedLessons.length >= MAX_LESSONS}
								/>
							}
							label={
								<Box
									display="flex"
									flexWrap="wrap"
									justifyContent="space-between"
									alignItems="center"
									gap="8px"
									sx={{ width: '100%', minWidth: 0 }}
								>
									<Box sx={{ minWidth: 0 }}>
										<Typography fontWeight={600}>
											{lesson.schedule_lesson_name || lesson.lesson_name}
										</Typography>
										{lesson.schedule_lesson_name &&
											lesson.schedule_lesson_name !== lesson.lesson_name && (
												<Typography variant="body2" color="text.secondary">
													{lesson.lesson_name}
												</Typography>
											)}
									</Box>
									{missedDate?.isValid && (
										<Typography variant="body2" color="text.secondary">
											{t('academyReworkings.missedOn', { date: missedDate.toFormat('dd.MM.yyyy') })}
										</Typography>
									)}
								</Box>
							}
							componentsProps={{ typography: { sx: { flex: 1, minWidth: 0, alignSelf: 'auto' } } }}
						/>
					</Paper>
				);
			})}
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				gap="16px"
				flexWrap="wrap"
			>
				<Chip
					label={t('academyReworkings.selectedLessons', { count: flow.selectedLessons.length })}
				/>
				<Button
					variant="contained"
					sx={nextButtonSx}
					disabled={!flow.selectedLessons.length}
					onClick={() => flow.setStep('days')}
				>
					{t('academyReworkings.nextDays')}
				</Button>
			</Box>
		</Box>
	);
}
